/**
 * src/lib/voice/logVoiceCaptureFailure.js — TICKET-020 client-side voice observability.
 *
 * Thin wrapper around logAppError with a stable schema for voice failure events.
 * Every catch path in useVoiceClassify (and its consumers like useProcurementMic,
 * useVoiceConversation) MUST call this so that mobile-only failures surface in
 * `app_error_logs` for diagnosis from a desktop session — `console.error` alone
 * never reaches the server, and iOS Safari has no remote console without a Mac
 * + USB Web Inspector.
 *
 * Fire-and-forget contract: this function never throws. If logAppError itself
 * throws, the failure is swallowed silently — the alternative would be a
 * catch-inside-a-catch race that could mask the original voice failure.
 */
import { logAppError } from '../errorReporter';

/**
 * @param {Object} params
 * @param {string} params.from_hook            — name of the calling hook (e.g. "useProcurementMic")
 * @param {string} params.context_type         — "procurement" | "task" | "pipeline" | "survey" | etc.
 * @param {string} params.context_hint         — fine-grained routing hint
 * @param {string} params.idempotency_key      — captureVoice idempotency key (for correlating start→end pairs)
 * @param {string} params.failure_stage        — "getUserMedia_denied" | "capture_timeout" | "poll_timeout" | "processing_error" | etc.
 * @param {Error|string|unknown} params.error  — the thrown value
 * @param {number} params.ms_in_processing     — wall-clock ms from setState('processing') to failure
 */
export function logVoiceCaptureFailure({
  from_hook,
  context_type,
  context_hint,
  idempotency_key,
  failure_stage,
  error,
  ms_in_processing,
}) {
  try {
    const isError = error instanceof Error;
    const error_name = isError ? (error.name || 'Error') : 'Unknown';
    const error_message = isError
      ? (error.message || '').slice(0, 2000)
      : String(error || '').slice(0, 2000);

    logAppError({
      type: 'voice_capture_failed',
      message: `[${from_hook}] ${failure_stage}: ${error_message.slice(0, 200)}`,
      stack: isError ? (error.stack || '').slice(0, 4000) : undefined,
      view: from_hook,
      metadata: {
        from_hook,
        context_type,
        context_hint,
        idempotency_key,
        failure_stage,
        error_name,
        error_message,
        ms_in_processing,
      },
    });
  } catch {
    // Fire-and-forget — logger failure must never re-throw and mask the
    // original voice failure that the caller is in the middle of handling.
  }
}
